import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { Download, Share, SquarePlus, X } from 'lucide-react'
import { usePWAInstall } from '@shared/hooks/usePWAInstall'

const DISMISS_KEY = 'isr-pwa-install-dismissed'
const DISMISS_DAYS = 14

const wasRecentlyDismissed = (): boolean => {
    try {
        const ts = window.localStorage.getItem(DISMISS_KEY)
        if (!ts) return false
        const ageMs = Date.now() - Number(ts)
        return ageMs < DISMISS_DAYS * 24 * 60 * 60 * 1000
    } catch {
        return false
    }
}

export default function InstallPrompt() {
    const { canInstall, isIos, promptInstall } = usePWAInstall()
    const [dismissed, setDismissed] = useState<boolean>(() => wasRecentlyDismissed())
    const [showIosHelp, setShowIosHelp] = useState(false)

    useEffect(() => {
        if (!canInstall && !isIos) setShowIosHelp(false)
    }, [canInstall, isIos])

    const visible = !dismissed && (canInstall || isIos)
    if (!visible) return null

    const dismiss = () => {
        try {
            window.localStorage.setItem(DISMISS_KEY, String(Date.now()))
        } catch {
        }
        setDismissed(true)
    }

    const handleInstall = async () => {
        if (isIos) {
            setShowIosHelp((s) => !s)
            return
        }
        const outcome = await promptInstall()
        if (outcome === 'accepted') setDismissed(true)
    }

    return (
        <AnimatePresence>
            <motion.div
                key="pwa-install"
                role="dialog"
                aria-label="Install Indian Studies Review"
                className="fixed inset-x-0 bottom-0 z-[9990] flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))]"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 24 }}
                transition={{ duration: 0.4, ease: [0.22, 0.8, 0.2, 1] }}
            >
                <div
                    className="relative w-full max-w-md overflow-hidden rounded-2xl border shadow-xl"
                    style={{
                        background: 'rgba(255,255,255,0.96)',
                        backdropFilter: 'blur(8px)',
                        borderColor: 'rgba(13,11,8,0.10)',
                        boxShadow: '0 18px 50px -12px rgba(13,11,8,0.28)'
                    }}
                >
                    <div className="flex items-start gap-3.5 p-4">
                        <div
                            className="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl"
                            style={{ border: '1px solid rgba(13,11,8,0.08)' }}
                        >
                            <img
                                src="/icons/pwa-192x192.png"
                                alt=""
                                aria-hidden
                                className="h-full w-full object-cover"
                            />
                        </div>

                        <div className="min-w-0 flex-1">
                            <p
                                className="font-display text-[15px] leading-tight"
                                style={{ color: 'var(--color-ink-700)', fontWeight: 600 }}
                            >
                                Install Indian Studies Review
                            </p>
                            <p
                                className="mt-1 text-[12.5px] leading-snug"
                                style={{ color: 'var(--color-ink-400)' }}
                            >
                                Add to your home screen for fast, full-screen, offline-ready access.
                            </p>

                            <div className="mt-3 flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={handleInstall}
                                    className="inline-flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-[12.5px] font-semibold text-white transition-opacity hover:opacity-90"
                                    style={{ background: 'var(--color-gold-500)' }}
                                >
                                    <Download size={15} strokeWidth={2.25} />
                                    {isIos ? 'How to install' : 'Install app'}
                                </button>
                                <button
                                    type="button"
                                    onClick={dismiss}
                                    className="rounded-lg px-3 py-2 text-[12.5px] font-semibold transition-colors hover:bg-black/5"
                                    style={{ color: 'var(--color-ink-400)' }}
                                >
                                    Not now
                                </button>
                            </div>

                            <AnimatePresence>
                                {isIos && showIosHelp && (
                                    <motion.ol
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        transition={{ duration: 0.25 }}
                                        className="mt-3 space-y-1.5 overflow-hidden text-[12.5px]"
                                        style={{ color: 'var(--color-ink-500)' }}
                                    >
                                        <li className="flex items-center gap-2">
                                            <span className="font-semibold">1.</span>
                                            <span className="flex items-center gap-1">
                                                Tap the <Share size={14} className="inline" /> Share button
                                            </span>
                                        </li>
                                        <li className="flex items-center gap-2">
                                            <span className="font-semibold">2.</span>
                                            <span className="flex items-center gap-1">
                                                Choose <SquarePlus size={14} className="inline" /> &ldquo;Add to Home
                                                Screen&rdquo;
                                            </span>
                                        </li>
                                    </motion.ol>
                                )}
                            </AnimatePresence>
                        </div>

                        <button
                            type="button"
                            onClick={dismiss}
                            aria-label="Dismiss install prompt"
                            className="-mr-1 -mt-1 shrink-0 rounded-full p-1.5 transition-colors hover:bg-black/5"
                            style={{ color: 'var(--color-ink-300)' }}
                        >
                            <X size={16} />
                        </button>
                    </div>

                    <div
                        aria-hidden
                        className="h-1 w-full"
                        style={{
                            background:
                                'linear-gradient(90deg, var(--color-gold-600), var(--color-gold-400))'
                        }}
                    />
                </div>
            </motion.div>
        </AnimatePresence>
    )
}
